import { getRoomSlug } from '../lib/roomSelection.ts';
import type { StrapiErrorBody } from './types.ts';

/**
 * Typed Strapi REST client (SPEC §3.4): JWT auth, token persistence, 401 -> re-auth, one retry
 * on a transient network blip for idempotent GETs, and Strapi's own error envelope surfaced as
 * a typed ApiError instead of a bare thrown Response.
 */

// Vite statically replaces `import.meta.env.*` at build time (types come from vite-env.d.ts).
// Node's own ESM support recognises `import.meta` too, so under `node --test` this line still
// evaluates safely — `import.meta.env` is just `undefined` there and we fall back below.
//
// There is no local Strapi to default to (see repo root .env.example): the fallback is a
// guaranteed-unreachable placeholder (RFC 2606 `.invalid`), not a guess at a local dev server, so
// a client that forgot to set VITE_API_URL fails fast with a clear network error instead of
// silently resolving relative paths against Vite's own dev-server origin.
export const API_BASE_URL: string = import.meta.env?.VITE_API_URL || 'http://backend.invalid';

const TOKEN_KEY = 'mvcp.jwt';
// VPS-10: `strapi-template/config/plugins.js` issues a 60-minute access token
// (`accessTokenLifespan: 3600`) and the operator decided to fix that on the client rather than
// raise the lifespan server-side. The refresh token this key stores comes back in the SAME JSON
// body as `jwt` (`config/plugins.js`'s `sessions.httpOnly: false` — see that file's own comment
// for why a cookie cannot work for this cross-origin SPA) and is rotated on every use
// (`@strapi/plugin-users-permissions` `auth.refresh`'s `rotateRefreshToken()`), so it is stored
// exactly like the access token: same storage helpers, same in-memory fallback under `node --test`.
const REFRESH_TOKEN_KEY = 'mvcp.refresh';

// localStorage exists in every real browser but not under `node --test` (no --experimental
// flag enables it there). Falling back to an in-memory Map keeps this module correct AND
// trivially unit-testable in Node with no mocking — tests just call setToken() directly.
const memoryStore = new Map<string, string>();

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

function storageGet(key: string): string | null {
  if (hasLocalStorage()) {
    try {
      return localStorage.getItem(key);
    } catch {
      /* private-mode/storage-disabled browsers: fall through to memory */
    }
  }
  return memoryStore.get(key) ?? null;
}

function storageSet(key: string, value: string): void {
  if (hasLocalStorage()) {
    try {
      localStorage.setItem(key, value);
      return;
    } catch {
      /* fall through to memory */
    }
  }
  memoryStore.set(key, value);
}

function storageRemove(key: string): void {
  if (hasLocalStorage()) {
    try {
      localStorage.removeItem(key);
      return;
    } catch {
      /* fall through to memory */
    }
  }
  memoryStore.delete(key);
}

/**
 * Strapi's upload `url` is an absolute CDN URL when the S3/Spaces provider is configured (the
 * production path — ARCHITECTURE.md §7: plain, permanent, public-read CDN URLs) but a
 * root-relative path like `/uploads/foo.mp4` under Strapi's own local-filesystem fallback
 * provider (used before DigitalOcean Spaces credentials are wired up on the VPS —
 * strapi-template/config/plugins.js). A relative URL must resolve against the Strapi origin, never
 * the page's own origin — `web/` is served by Vite on a different port — so this is the one
 * place that decision is made; every consumer of a media/poster URL must go through it.
 */
export function resolveMediaUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

export function getToken(): string | null {
  return storageGet(TOKEN_KEY);
}

export function setToken(token: string): void {
  storageSet(TOKEN_KEY, token);
}

export function clearToken(): void {
  storageRemove(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return storageGet(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token: string): void {
  storageSet(REFRESH_TOKEN_KEY, token);
}

export function clearRefreshToken(): void {
  storageRemove(REFRESH_TOKEN_KEY);
}

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: Record<string, unknown>;

  constructor(status: number, message: string, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Fires whenever a request comes back 401 — App uses this to drop into the reauth state. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  body?: BodyInit | Record<string, unknown> | null;
  baseUrl?: string;
  /** Retry exactly once on a network-level failure. Defaults to true for GET, false otherwise —
   * mutating requests must never be silently replayed. */
  retry?: boolean;
  /**
   * P0 fix (round 3): defaults to `true` (unchanged behaviour). Set `false` for an endpoint whose
   * Strapi route is granted to the Public role but NOT to the caller's own authenticated role —
   * confirmed live for `GET /api/catalog/songs/:documentId` (`catalog.getSong`): the exact same URL
   * returns 200 for an anonymous caller and 403 for a signed-in KJ, because Strapi's permission
   * check uses the AUTHENTICATED user's role the instant a valid `Authorization` header is present,
   * never falling back to what Public would have allowed (`strapi-template/src/bootstrap/roles.js`
   * grants `catalog.getSong` only to Public, not `kj` — a server-side fix out of this wave's reach,
   * this wave is web/-only). Omitting the header entirely makes Strapi treat the request as
   * anonymous, which this endpoint already allows. See `api/songs.ts`'s `getSongMedia` for the
   * one caller that needs this.
   */
  auth?: boolean;
}

/** Extracts a bare host (`api.example.com`, `localhost:1337`) from a base URL — the one copy of
 * this logic, shared with `realtime.ts` (re-exported there for `RealtimeClient.getTargetHost()`/
 * `realtime.test.ts`, which both predate this module having its own copy) and `api/guest.ts`, so
 * the offline indicator's DESIGN.md §6.4 copy ("Offline · cdn.example.com unreachable") names the
 * failing host identically whether the request came from `apiFetch`, the SSE stream, or the guest
 * page's own fetch. Falls back to the raw input if it is not a parseable absolute URL. */
export function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * MVP-390: the one place a dead fetch becomes an `ApiError`, for `doFetch()` below and for
 * `api/guest.ts`, which used to hold a hand-copied duplicate of the sentence.
 *
 * R7-P2-4's finding is kept and only re-expressed: `fetch` throws the same generic "network error"
 * for two very different failures — the browser genuinely has no connection, AND a 5xx that never
 * sent CORS headers (confirmed live: a bare 503 restart blip). `navigator.onLine` tells them apart
 * well enough to stop telling a KJ with a perfectly good connection to check it.
 *
 * What changed is that the difference is now a CODE, not a sentence. Both cases already travelled
 * with `'NETWORK_ERROR'` and an English sentence beside it; the sentence was the part that got
 * rendered, in a Thai-default UI, from a `.ts` file no i18n gate was reading. `errorMessage.ts`
 * resolves these two codes to dictionary entries, and the host rides in `details` so the entry can
 * still name the machine that is not answering (DESIGN.md §6.4).
 *
 * `Error.message` is the code and the host, unpunctuated: for a stack trace, never for a screen.
 */
export function networkError(url: string): ApiError {
  const host = hostOf(url);
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const code = offline ? 'OFFLINE' : 'SERVER_UNREACHABLE';
  return new ApiError(0, `${code} ${host}`, code, { host });
}

/**
 * R9-P1-D fix (round 9 critic, reproduced live): `ConnectionIndicator` used to derive its whole
 * "Offline · host unreachable" reading purely from `RealtimeClient`'s SSE state — confirmed live,
 * it read "Offline" while every REST call was returning 200. REST reachability and the SSE
 * connection are two genuinely different things (one dropped stream does not mean the backend is
 * down), so this is the REST side's own signal: every `doFetch()` call updates it, and
 * `ConnectionIndicator` subscribes independently of whatever `realtimeClient.getState()` says.
 *
 * MVP-483 corrects this comment. It used to claim "and every guest-page fetch too, since
 * `api/guest.ts` calls through this same module" — `api/guest.ts` imports `ApiError`/
 * `API_BASE_URL`/`networkError` from here (data, a constant and a pure factory), but its
 * `guestFetch` calls bare `fetch` and never `doFetch`, so nothing on the guest route ever moved
 * this value and `getReachability()` was permanently `'unknown'` there. It cannot route through
 * `apiFetch` (that would attach the operator's bearer token to a guest request — see guest.ts's
 * own module doc comment), so it reports reachability itself through the two wrappers below.
 */
export type ReachabilityState = 'reachable' | 'unreachable' | 'unknown';

let reachability: ReachabilityState = 'unknown';
const reachabilityListeners = new Set<(state: ReachabilityState) => void>();

function setReachability(next: ReachabilityState): void {
  if (next === reachability) return;
  reachability = next;
  for (const listener of reachabilityListeners) listener(next);
}

/** The REST layer's own last-known reachability — 'unknown' until the very first `doFetch()` call
 * of the session resolves either way. */
export function getReachability(): ReachabilityState {
  return reachability;
}

export function onReachabilityChange(listener: (state: ReachabilityState) => void): () => void {
  reachabilityListeners.add(listener);
  return () => reachabilityListeners.delete(listener);
}

/**
 * MVP-483. The two verbs `doFetch()` below performs on itself, exported for the ONE other fetch
 * helper in this app that cannot go through `doFetch` — `api/guest.ts`'s `guestFetch` (see the
 * `ReachabilityState` doc comment above, and that module's own, for why it stays separate). Thin on
 * purpose: `setReachability` itself stays private so no caller can invent a third state or reset the
 * value to `'unknown'` after a request has already answered the question.
 */
export function markReachable(): void {
  setReachability('reachable');
}

export function markUnreachable(): void {
  setReachability('unreachable');
}

async function doFetch(url: string, init: RequestInit): Promise<Response> {
  try {
    const res = await fetch(url, init);
    // Any actual HTTP response — even a 4xx/5xx — proves the server itself is reachable; only a
    // thrown `fetch()` (the `catch` below) means it genuinely was not.
    setReachability('reachable');
    return res;
  } catch (err) {
    // MVP-336: an abort is the CALLER stopping, not the network failing. `fetch` rejects with an
    // `AbortError` DOMException, which without this branch was flattened into the same
    // `ApiError(0, 'NETWORK_ERROR')` as a genuine outage — so cancelling an ingest run would have
    // told the operator the server was unreachable AND driven ConnectionIndicator offline
    // (§6.4) over a button they pressed on purpose. Rethrown untouched, and reachability is left
    // exactly as the last real request found it.
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    setReachability('unreachable');
    throw networkError(url);
  }
}

interface RefreshTokenResponse {
  jwt: string;
  refreshToken?: string;
}

// VPS-10: `POST /api/auth/refresh` and the login call it follows from must never themselves
// trigger another refresh attempt on a 401 — refresh has no refresh token to fall back to, and
// login's own 401/400 means the submitted credentials were wrong, not that a live session expired.
const REFRESH_EXEMPT_PATHS = new Set(['/api/auth/local', '/api/auth/refresh']);

// A single in-flight refresh shared across every concurrent 401 — several requests can be
// in flight when the access token expires (e.g. a batch of catalog fetches), and each one
// hitting `/api/auth/refresh` independently would race to rotate the SAME refresh token
// (`rotateRefreshToken()` on the server is one-time-use), so every caller but the first would
// get "Invalid refresh token" instead of a valid retry. Module-scoped rather than per-apiFetch-call
// so it is genuinely shared across all of them.
let refreshPromise: Promise<string> | null = null;

async function performRefresh(baseUrl: string): Promise<string> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new ApiError(401, 'No refresh token available', 'NO_REFRESH_TOKEN');
  }

  const res = await doFetch(`${baseUrl}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!res.ok) {
    throw new ApiError(res.status, 'Session refresh failed');
  }

  const body = (await res.json()) as RefreshTokenResponse;
  setToken(body.jwt);
  // The server rotates the refresh token on every use (`auth.refresh`'s `rotateRefreshToken()`) —
  // if it comes back, it REPLACES the one just spent; the old one is no longer valid.
  if (body.refreshToken) setRefreshToken(body.refreshToken);
  return body.jwt;
}

/** Exported for `realtime.ts`: the SSE stream authenticates with its own `fetch()` call (never
 * through `apiFetch`, since `EventSource` cannot send an `Authorization` header — see that
 * file's own comment), so it needs this same dedup'd refresh directly rather than duplicating it. */
export function refreshAccessToken(baseUrl: string): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = performRefresh(baseUrl).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * MVP-472 follow-up 2. Every operator call says which ROOM this screen is driving.
 *
 * Appended here rather than at each call site because "which room" is a property of the SCREEN, not
 * of any one endpoint: the transport, the session it starts and the realtime stream it watches all
 * have to agree, and a room param that some callers remembered and others forgot is exactly how an
 * ops app ends up reading room 3's playback while its own buttons drive room 5. Endpoints that do
 * not scope by room simply ignore it.
 *
 * Never on a guest call: the guest page has its own `guestFetch` (api/guest.ts) and never reaches
 * this function, and a request that is explicitly `auth: false` is not acting as an operator either.
 * A screen that has picked nothing sends nothing, which is the pre-picker behaviour exactly.
 *
 * MVP-573: this param now carries the VENUE scope as well, and that is load-bearing rather than a
 * side effect. No caller in this app has ever sent `?venue=`, so the day a customer's `shop` account
 * covers two branches (MVP-509's "เพิ่มสาขา") it names no venue and `venueIdForRequest`
 * (strapi-template/src/utils/venue-scope.js) refuses it — `403 VENUE_NOT_IN_SCOPE` on every
 * `getActiveSession()` poll. The server now falls back to the venue that owns this room, because a
 * room belongs to exactly one. So dropping the param for the endpoints that "do not scope by room"
 * would silently take a multi-branch shop's console off the air; it stays on every authenticated
 * call. The remaining gap is a browser that has picked NO room yet, which needs an operator-facing
 * branch picker (api/rooms.ts says it does not exist) and is not this function's to close.
 */
function withRoomScope(path: string, options: ApiFetchOptions): string {
  if (options.auth === false) return path;
  const slug = getRoomSlug();
  if (!slug) return path;
  return `${path}${path.includes('?') ? '&' : '?'}room=${encodeURIComponent(slug)}`;
}

export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const base = options.baseUrl ?? API_BASE_URL;
  const url = `${base}${withRoomScope(path, options)}`;
  const method = (options.method ?? 'GET').toUpperCase();
  const shouldRetry = options.retry ?? method === 'GET';

  const headers = new Headers(options.headers);
  const isPlainObjectBody =
    options.body !== null && typeof options.body === 'object' && !(options.body instanceof FormData) && !(options.body instanceof Blob);
  if (isPlainObjectBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const token = options.auth === false ? null : getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const init: RequestInit = {
    ...options,
    method,
    headers,
    body: isPlainObjectBody ? JSON.stringify(options.body) : (options.body as BodyInit | null | undefined),
  };

  let res: Response;
  try {
    res = await doFetch(url, init);
  } catch (err) {
    if (!shouldRetry) throw err;
    res = await doFetch(url, init); // exactly one retry for a transient blip
  }

  // VPS-10: a 401 here means the 60-minute access token has lapsed (config/plugins.js's
  // `accessTokenLifespan: 3600`, deliberately not raised — see that file's comment). Try exactly
  // one refresh-and-replay before giving up, so a still-valid session does not force a re-login
  // every hour. Never for the login/refresh calls themselves (REFRESH_EXEMPT_PATHS) and never
  // without a stored refresh token to spend.
  if (res.status === 401 && options.auth !== false && !REFRESH_EXEMPT_PATHS.has(path) && getRefreshToken()) {
    try {
      const freshToken = await refreshAccessToken(base);
      const retryHeaders = new Headers(init.headers);
      retryHeaders.set('Authorization', `Bearer ${freshToken}`);
      res = await doFetch(url, { ...init, headers: retryHeaders });
    } catch {
      // Refresh itself failed — fall through to the standard 401 handling below, which clears
      // both tokens and notifies listeners exactly as if this whole block never ran.
    }
  }

  // `auth: false` never sent a token in the first place, so a 401 here says nothing about whatever
  // session might otherwise be stored — clearing it and firing the reauth listeners would log an
  // operator out over a call that was never using their session.
  if (res.status === 401 && options.auth !== false) {
    clearToken();
    clearRefreshToken();
    for (const listener of unauthorizedListeners) listener();
  }

  if (!res.ok) {
    // P2 fix (round 4): read as text FIRST, then attempt JSON.parse on that text — a `res.json()`
    // that fails to parse consumes/discards the body with no way to look at it afterwards. A 5xx
    // straight from a proxy/load-balancer (nginx, the dev-server's own proxy) in front of Strapi is
    // routinely a plain-text or HTML error page, not Strapi's JSON envelope, which is exactly the
    // "capture the proxy's 503 body" case below needs to see.
    let rawText: string | undefined;
    let body: StrapiErrorBody | undefined;
    try {
      rawText = await res.text();
      body = JSON.parse(rawText) as StrapiErrorBody;
    } catch {
      /* not valid JSON — rawText (if read) still holds whatever the proxy/server actually sent */
    }
    const code = (body?.error?.details?.code as string | undefined) ?? body?.error?.name;
    // P2 fix (round 3): a 5xx (503, seen live on the very first request of an otherwise-healthy
    // page load — backend/proxy warm-up, out of web/'s reach to actually fix) used to leave no
    // trace at all once a caller's own retry/backoff quietly succeeded a moment later (defect:
    // "client retry masks it" — RealtimeClient's reconnect loop is exactly that caller; see its
    // own matching log in realtime.ts). One line, visible in devtools, so the NEXT time this
    // happens it is not invisible.
    if (res.status >= 500) {
      // P2 fix (round 4): include a short snippet of the raw body — this was `${res.status}` alone
      // before, which said a 503 happened but nothing about why, and a non-JSON proxy error page
      // (the common case for the very first request while Strapi/its proxy is still warming up)
      // used to vanish entirely into the swallowed `res.json()` catch above.
      const snippet = rawText ? ` body=${rawText.slice(0, 200)}` : '';
      console.warn(`apiFetch: ${method} ${path} -> ${res.status}${snippet}`);
    }
    throw new ApiError(res.status, body?.error?.message ?? `Request failed with status ${res.status}`, code, body?.error?.details);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
